import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useState } from "react";

/**
 * GaleriaImagen es una foto de la galería del LOTO (#212): su URL autenticada (cookie mismo-origen,
 * usable directo como `<img src>`), un texto alternativo y el pie que la describe en el lightbox.
 * `origen` la clasifica (foto del LOTO vs foto de un candado) para filtrarla y etiquetarla.
 */
export type GaleriaImagen = {
  key: string;
  src: string;
  alt: string;
  origen: "loto" | "candado";
  titulo: string;
  pie?: string;
};

/**
 * Lightbox muestra una foto a pantalla completa sobre un fondo oscuro, con navegación anterior/
 * siguiente (botones grandes para guantes, flechas del teclado y gesto de deslizar en móvil), cierre
 * (botón, Escape o clic en el fondo) y un pie con los metadatos. Sin librerías externas. Se usa tanto
 * para la galería agregada del LOTO como para las miniaturas de las fotos de verificación (#212).
 *
 * `index` null ⇒ cerrado (no renderiza nada). El caller controla el índice para poder abrirlo en una
 * foto concreta; `onIndexChange` lo mueve dentro de [0, imagenes.length).
 */
export function Lightbox({
  imagenes,
  index,
  onClose,
  onIndexChange,
}: {
  imagenes: GaleriaImagen[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}) {
  const abierto = index != null && index >= 0 && index < imagenes.length;
  // Coordenada X donde empieza el gesto de deslizar (móvil), para decidir prev/siguiente al soltar.
  const [inicioX, setInicioX] = useState<number | null>(null);

  const irAnterior = useCallback(() => {
    if (index == null) {
      return;
    }
    onIndexChange((index - 1 + imagenes.length) % imagenes.length);
  }, [index, imagenes.length, onIndexChange]);

  const irSiguiente = useCallback(() => {
    if (index == null) {
      return;
    }
    onIndexChange((index + 1) % imagenes.length);
  }, [index, imagenes.length, onIndexChange]);

  // Teclado: Escape cierra, flechas navegan. Solo activo mientras está abierto.
  useEffect(() => {
    if (!abierto) {
      return;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        irAnterior();
      } else if (e.key === "ArrowRight") {
        irSiguiente();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [abierto, onClose, irAnterior, irSiguiente]);

  if (!abierto) {
    return null;
  }

  const actual = imagenes[index];
  const hayVarias = imagenes.length > 1;

  function onPointerDown(e: ReactPointerEvent) {
    setInicioX(e.clientX);
  }
  function onPointerUp(e: ReactPointerEvent) {
    if (inicioX == null) {
      return;
    }
    const dx = e.clientX - inicioX;
    setInicioX(null);
    // Umbral de 50px: por debajo se considera un toque, no un deslizamiento.
    if (hayVarias && Math.abs(dx) > 50) {
      if (dx > 0) {
        irAnterior();
      } else {
        irSiguiente();
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/95">
      {/* Fondo: un botón dedicado para cerrar (accesible, sin onClick en un elemento estático). */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
      />

      {/* Barra superior: contador + cerrar. */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 text-white">
        <span className="font-medium text-sm tabular-nums">
          {index + 1} / {imagenes.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <X className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Zona de la imagen: navegación por botones laterales y por deslizamiento. */}
      <div className="relative z-10 flex flex-1 items-center justify-center overflow-hidden px-2">
        {hayVarias ? (
          <button
            type="button"
            onClick={irAnterior}
            aria-label="Foto anterior"
            className="absolute left-2 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronLeft className="h-8 w-8" aria-hidden="true" />
          </button>
        ) : null}
        <img
          src={actual.src}
          alt={actual.alt}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          className="max-h-full max-w-full touch-pan-y select-none object-contain"
        />
        {hayVarias ? (
          <button
            type="button"
            onClick={irSiguiente}
            aria-label="Foto siguiente"
            className="absolute right-2 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <ChevronRight className="h-8 w-8" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {/* Pie con los metadatos de la foto. */}
      <div className="relative z-10 flex flex-col gap-0.5 px-4 py-4 text-white">
        <span className="font-semibold text-base">{actual.titulo}</span>
        {actual.pie ? <span className="text-slate-300 text-sm">{actual.pie}</span> : null}
      </div>
    </div>
  );
}
